import "../chat-thread";

import { Storybook } from "@tento-ui/ui/storybook";
import { ChatThread } from "../chat-thread";
import type { ChatMessage } from "../chat-types";
import { CodexLook } from "./codex";

// The codex look, rendering the grammar it now owns.
//
// Every block below is a plain canonical block — `text`, `tool_call` — and
// nothing in the messages says "codex". What draws the dim bullet, the
// `• Ran …` line and the `└` connector is the look: it names its own turn
// element, which routes those two block types onto the two plugins the look
// declares. Bind a different theme to the same thread and the same messages
// come out in that theme's grammar instead, which is the property this look
// exists to demonstrate.
const SESSION: ChatMessage[] = [
  {
    id: "m1",
    role: "user",
    content: [{ type: "text", text: "check the click producer and show me the tail" }],
    isStreaming: false,
    createdAt: 0,
  },
  {
    id: "m2",
    role: "assistant",
    content: [
      {
        type: "text",
        text:
          "The click producer is working: selection.ljson and selection-item.json " +
          "both advanced on the last run, so the gesture path is live end to end.",
      },
      {
        type: "tool_call",
        tool: "shell",
        command: "zsh -lc 'tail -n 40 .ux-tests/cow/cow-edit-keeps-focus/ledger.ljson'",
        output: [
          "step 12  click  row=r_4812  ok",
          "step 13  type   \"quarterly\"  ok",
          "step 14  blur   focus retained  ok",
          "3 assertions, 0 failures",
        ].join("\n"),
        status: "completed",
      },
    ],
    isStreaming: false,
    createdAt: 1,
  },
];

const FAILING: ChatMessage[] = [
  {
    id: "m3",
    role: "assistant",
    content: [
      { type: "text", text: "Re-running the suite against the rebuilt bundle." },
      {
        type: "tool_call",
        tool: "shell",
        command: "zsh -lc 'tsh.lona lona ts tests skin-contract'",
        output: null,
        status: "running",
      },
      {
        type: "tool_call",
        tool: "shell",
        command: "zsh -lc 'tsh.lona lona ts check'",
        output: "error TS2339: Property 'bind' does not exist on type 'LonaWebComponent'.",
        status: "failed",
      },
    ],
    isStreaming: false,
    createdAt: 2,
  },
];

function $codex(messages: ChatMessage[]): HTMLElement {
  const $thread = ChatThread.make() as ChatThread;
  $thread.theme = CodexLook;
  void $thread.setMessages(messages.map((m) => ({ ...m })));
  // No padding on the thread — that is the contract. Rows carry their own
  // gutter (--chat-turn-inset, which the look sets) and the vertical room is
  // the scroll viewport's buffer.
  return $thread.assignStyles({
    display: "block",
    height: "320px",
    borderRadius: "10px",
    boxSizing: "border-box",
  });
}

export class CodexLookStoryBook extends Storybook.Default {
  title(): string {
    return "Chat look — Codex CLI";
  }

  examples(): Storybook.Example[] {
    return [
      {
        // The user band, assistant prose behind its bullet, and a settled
        // tool call collapsed to its summary line. Click the entry to expand.
        title: "A session",
        render: () => $codex(SESSION),
      },
      {
        // The two states the summary line has to carry on its own: a call
        // still in flight (the head pulses) and one that failed (✗ in the
        // connector). Neither is a different block type — same plugin.
        title: "Running, and failed",
        render: () => $codex(FAILING),
      },
    ];
  }
}
